import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { useCurrencyMaster } from '../../../hooks/useCurrencyMaster';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import { useFactoryDetailsMaster } from '../../../hooks/useFactoryDetailsMaster';
import { getAvailableRoles } from '../../../utils/roleSwitcher';
import '../../../assets/styles/invoice_list.scss';
import '../../../assets/styles/shipping_manifest_generation_list.scss';
import '../../../assets/styles/shipping_manifest_create.scss';

export default function ShippingManifestGenerationList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [acceptedEWayBills, setAcceptedEWayBills] = useState([]);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [selectedManifestEWayBillNumber, setSelectedManifestEWayBillNumber] = useState(null);
  const [showManifestModal, setShowManifestModal] = useState(false);
  const [showAcceptanceModal, setShowAcceptanceModal] = useState(false);
  
  // Master Data Hooks
  const { currencyOptions } = useCurrencyMaster();
  const { attributeUnitOptions } = useProductDetailsMaster();
  const { shippingTermsOptions, modeOfTransportOptions } = useFactoryDetailsMaster();

  const roles = getAvailableRoles();
  const isLogistics =
    Array.isArray(roles) && roles.some((r) => String(r?.id || '').toLowerCase() === 'logistics');

  // Helper function to get unit label
  const getUnitLabel = (unitValue) => {
    if (!unitValue) return '';
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) return unitValue;
    
    const unitValueNum = isNaN(unitValue) ? null : Number(unitValue);
    const foundByValue = attributeUnitOptions.find(opt => {
      if (String(opt.value) === String(unitValue)) return true;
      if (unitValueNum !== null && !isNaN(opt.value)) {
        return Number(opt.value) === unitValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      return foundByValue.label || unitValue;
    }
    
    const foundByLabel = attributeUnitOptions.find(opt => 
      opt.label === String(unitValue) || 
      opt.label?.toLowerCase() === String(unitValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return unitValue;
  };

  // Helper function to get currency symbol
  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return '';
    if (currencyOptions && currencyOptions.length > 0) {
      const foundByCode = currencyOptions.find(opt => 
        opt.currencyCode === String(currencyCode).toUpperCase() ||
        String(opt.value) === String(currencyCode) ||
        opt.currencyCode === String(currencyCode)
      );
      if (foundByCode && foundByCode.currencySymbol) {
        return foundByCode.currencySymbol;
      }
    }
    return '';
  };

  // Helper function to fetch full PO details with RFQ data
  const fetchFullPODetails = async (order) => {
    try {
      const poId = order.id || order.purchase_order_id;
      if (!poId) return order;
      
      const originalRfqId = order.rfq_id;
      
      const fullPOResponse = await CommonService.get(`/purchase-order/${poId}`);
      if (fullPOResponse && fullPOResponse.success && fullPOResponse.data) {
        const finalRfqId = originalRfqId || fullPOResponse.data.rfq_id || null;
        
        let rfqData = null;
        if (finalRfqId) {
          try {
            const rfqIdNum = typeof finalRfqId === 'string' && finalRfqId.startsWith('REQ-')
              ? parseInt(finalRfqId.replace('REQ-', ''), 10)
              : parseInt(finalRfqId, 10);
            
            if (!isNaN(rfqIdNum)) {
              const rfqResponse = await CommonService.get(`/rfq/${rfqIdNum}`);
              if (rfqResponse && rfqResponse.success && rfqResponse.data) {
                rfqData = rfqResponse.data;
              }
            }
          } catch (rfqError) {
            console.warn(`Error fetching RFQ ${finalRfqId} for PO ${poId}:`, rfqError);
          }
        }
        
        return {
          ...order,
          ...fullPOResponse.data,
          rfq_id: finalRfqId,
          rfq_data: rfqData,
          line_items: fullPOResponse.data.line_items || fullPOResponse.data.po_data?.lineItems || [],
          po_data: {
            ...order.po_data,
            ...fullPOResponse.data.po_data,
            lineItems: fullPOResponse.data.line_items || fullPOResponse.data.po_data?.lineItems || []
          }
        };
      }
      return order;
    } catch (error) {
      console.error(`Error fetching full PO details for PO ${order.id}:`, error);
      return order;
    }
  };

  // Fetch invoices for a purchase order
  const fetchInvoicesForPO = async (poId) => {
    try {
      const response = await CommonService.get(`/seller-invoice/purchase-order/${poId}`);
      if (response && response.success && response.data) {
        return Array.isArray(response.data) ? response.data : [];
      }
      return [];
    } catch (error) {
      console.warn(`Error fetching invoices for PO ${poId}:`, error);
      return [];
    }
  };

  // Fetch E-Way Bills for an invoice
  const fetchEWayBillsForInvoice = async (invoiceId) => {
    try {
      const response = await CommonService.get(`/eway-bill/invoice/${invoiceId}`);
      if (response && response.success && response.data) {
        const ewayBills = Array.isArray(response.data) ? response.data : [response.data];
        return ewayBills;
      }
      return [];
    } catch (error) {
      console.warn(`Error fetching E-Way Bills for invoice ${invoiceId}:`, error);
      return [];
    }
  };

  // Fetch shipping manifests for an invoice
  const fetchShippingManifestsForInvoice = async (invoiceId) => {
    try {
      const response = await CommonService.get(`/shipping-manifest/invoice/${invoiceId}`);
      if (response && response.success && response.data) {
        const manifests = Array.isArray(response.data) ? response.data : [response.data];
        // Return the latest manifest (most recent)
        return manifests.length > 0 ? manifests[0] : null;
      }
      return null;
    } catch (error) {
      console.warn(`Error fetching shipping manifests for invoice ${invoiceId}:`, error);
      return null;
    }
  };

  // Fetch generated e-way bills (not buyer accepted)
  const fetchAcceptedEWayBills = async () => {
    try {
      setLoading(true);

      // Logistics flow only: use logistics E-Way Bills (invoice_id can be null).
      if (isLogistics) {
        const res = await CommonService.get('/eway-bill/logistics/provider/my-eway-bills');
        const bills = Array.isArray(res?.data) ? res.data : res?.data?.data;
        const arr = Array.isArray(bills) ? bills : [];
        const nonDraft = arr.filter((b) => String(b?.status || '').trim().toLowerCase() !== 'draft');
        setAcceptedEWayBills(
          nonDraft.map((b) => ({
            ...b,
            order: {},
            invoice: {},
            poId: null,
            invoiceId: b?.invoice_id ?? null,
            shippingManifest: null,
          }))
        );
        return;
      }

      const poResponse = await CommonService.get("/purchase-order/seller/my-orders");

      if (poResponse && poResponse.success && poResponse.data) {
        const allOrders = Array.isArray(poResponse.data) ? poResponse.data : [];

        // Filter only accepted orders
        const accepted = allOrders.filter(order => {
          const status = (order.po_status || '').toLowerCase();
          const responseStatus = (order.po_response_status || '').toLowerCase();
          return status === 'accepted' || responseStatus === 'accepted';
        });

        // Fetch full PO details (including line items and RFQ data)
        const acceptedWithDetails = await Promise.all(
          accepted.map(order => fetchFullPODetails(order))
        );

        // Fetch invoices and eway bills for each PO
        const allAcceptedEWayBills = [];

        for (const order of acceptedWithDetails) {
          const poId = order.id || order.purchase_order_id;
          if (!poId) continue;

          const invoices = await fetchInvoicesForPO(poId);

          for (const invoice of invoices) {
            // Only process accepted invoices
            if (invoice.status && invoice.status.toLowerCase().trim() === 'accepted') {
              const ewayBills = await fetchEWayBillsForInvoice(invoice.id);

              // Keep any generated e-way bills (exclude drafts). Buyer acceptance is not required here.
              const acceptedEWayBills = ewayBills.filter(ewayBill => {
                const status = (ewayBill.status || '').trim().toLowerCase();
                return status !== 'draft';
              });

              // Fetch shipping manifest for this invoice
              const shippingManifest = await fetchShippingManifestsForInvoice(invoice.id);

              // Add order and invoice context to each accepted eway bill
              for (const ewayBill of acceptedEWayBills) {
                allAcceptedEWayBills.push({
                  ...ewayBill,
                  order: order,
                  invoice: invoice,
                  poId: poId,
                  invoiceId: invoice.id,
                  shippingManifest: shippingManifest
                });
              }
            }
          }
        }

        setAcceptedEWayBills(allAcceptedEWayBills);
      } else {
        setAcceptedEWayBills([]);
      }
    } catch (error) {
      console.error('Error fetching accepted eway bills:', error);
      toast.error('Failed to fetch accepted eway bills');
      setAcceptedEWayBills([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAcceptedEWayBills();
  }, []);

  // Transform eway bill data for display
  const transformEWayBillData = (ewayBillData) => {
    const order = ewayBillData.order || {};
    const invoice = ewayBillData.invoice || {};
    const ewayBill = ewayBillData;
    
    let poData = {};
    let totalAmount = 0;
    let lineItems = [];
    
    try {
      if (order.po_data) {
        poData = order.po_data;
        if (poData.lineItems && Array.isArray(poData.lineItems) && poData.lineItems.length > 0) {
          lineItems = poData.lineItems;
        }
      }
      if (order.line_items && Array.isArray(order.line_items) && order.line_items.length > 0) {
        lineItems = order.line_items;
      }
      
      // Also get line items from invoice if available
      if (invoice && invoice.line_items && Array.isArray(invoice.line_items)) {
        lineItems = invoice.line_items;
      }
      
      if (lineItems && lineItems.length > 0) {
        totalAmount = lineItems.reduce((sum, item) => {
          if (item.total !== undefined && item.total !== null && item.total !== '') {
            const itemTotal = parseFloat(item.total);
            if (!isNaN(itemTotal) && itemTotal > 0) {
              return sum + itemTotal;
            }
          }
          
          const unitPrice = parseFloat(item.unit_price || item.unitPrice) || 0;
          const quantity = parseFloat(item.quantity) || 0;
          const discount = parseFloat(item.discount) || 0;
          const subtotal = unitPrice * quantity;
          const discountAmount = subtotal * (discount / 100);
          const taxableAmount = subtotal - discountAmount;
          
          const cgstAmount = parseFloat(item.cgst_amount || item.cgstAmount) || 0;
          const sgstAmount = parseFloat(item.sgst_amount || item.sgstAmount) || 0;
          const igstAmount = parseFloat(item.igst_amount || item.igstAmount) || 0;
          const cessAmount = parseFloat(item.cess_amount || item.cessAmount) || 0;
          const customsAmount = parseFloat(item.customs_amount || item.customsAmount) || 0;
          const bcdAmount = parseFloat(item.bcd_amount || item.bcdAmount) || 0;
          const swsAmount = parseFloat(item.sws_amount || item.swsAmount) || 0;
          const taxAmount = parseFloat(item.tax_amount || item.taxAmount) || 0;
          
          if (item.assessable_value !== undefined && item.assessable_value !== null && item.assessable_value !== '') {
            const assessableValue = parseFloat(item.assessable_value || item.assessableValue) || 0;
            const igstOnAssessable = parseFloat(item.igst_amount || item.igstAmount) || 0;
            const cessOnAssessable = parseFloat(item.cess_amount || item.cessAmount) || 0;
            return sum + (assessableValue + igstOnAssessable + cessOnAssessable);
          }
          
          return sum + (taxableAmount + cgstAmount + sgstAmount + igstAmount + cessAmount + customsAmount + bcdAmount + swsAmount + taxAmount);
        }, 0);
      }
    } catch (e) {
      console.warn('Error parsing PO data:', e);
    }
    
    // Use invoice currency if available, otherwise use PO currency
    const currency = invoice.currency || poData.currency || order.currency || 'INR';
    const currencyCode = getCurrencyLabel(currency);
    
    // Use invoice grand_total if available, otherwise calculate from line items
    const displayTotalAmount = invoice.grand_total !== null && invoice.grand_total !== undefined 
      ? invoice.grand_total 
      : totalAmount;
    
    // Extract product names - prioritize RFQ data
    let productNames = [];
    if (order.rfq_data && order.rfq_data.product_name) {
      const rfqProductName = order.rfq_data.product_name.trim();
      if (rfqProductName && rfqProductName !== '' && rfqProductName !== 'N/A') {
        let cleanedProductName = rfqProductName
          .replace(/\bHSN\s*[:\-]?\s*\d{4,8}\b/gi, '')
          .replace(/\b\d{4,8}\s*\(HSN\)/gi, '')
          .replace(/\bHSN\s*Code\s*[:\-]?\s*\d{4,8}\b/gi, '')
          .trim();
        cleanedProductName = cleanedProductName.replace(/\s+/g, ' ').trim();
        if (cleanedProductName && cleanedProductName !== '') {
          productNames.push(cleanedProductName);
        }
      }
    }
    
    if (productNames.length === 0 && lineItems && lineItems.length > 0) {
      lineItems.forEach(item => {
        let productName = item.description || item.productName || item.product_name || item.name || '';
        if (productName) {
          productName = productName
            .replace(/\bHSN\s*[:\-]?\s*\d{4,8}\b/gi, '')
            .replace(/\b\d{4,8}\s*\(HSN\)/gi, '')
            .replace(/\bHSN\s*Code\s*[:\-]?\s*\d{4,8}\b/gi, '')
            .trim();
          productName = productName.replace(/\s+/g, ' ').trim();
        }
        if (productName && productName.trim() !== '' && productName !== 'N/A') {
          if (!productNames.includes(productName.trim())) {
            productNames.push(productName.trim());
          }
        }
      });
    }
    
    const productNamesDisplay = productNames.length > 0 
      ? (productNames.length > 3 ? productNames.slice(0, 3).join(', ') + ` +${productNames.length - 3} more` : productNames.join(', '))
      : 'N/A';
    
    // Extract product categories
    let productCategories = [];
    if (lineItems && lineItems.length > 0) {
      lineItems.forEach(item => {
        const category = item.category || item.productCategory || item.product_category || item.category_name || '';
        if (category && category.trim() !== '' && !productCategories.includes(category.trim())) {
          productCategories.push(category.trim());
        }
      });
    }
    
    if (productCategories.length === 0) {
      const rfqData = order.rfq_data || order.rfqData;
      if (rfqData) {
        const categoryName = rfqData.category_name || rfqData.categoryName || '';
        if (categoryName && categoryName.trim() !== '') {
          productCategories.push(categoryName.trim());
        }
      }
    }
    
    const productCategoriesDisplay = productCategories.length > 0 
      ? productCategories.join(', ')
      : 'N/A';
    
    // Calculate quantity from line items
    let totalQuantity = 0;
    let quantityUnit = '';
    if (lineItems && lineItems.length > 0) {
      lineItems.forEach(item => {
        const itemQuantity = parseFloat(item.quantity) || 0;
        totalQuantity += itemQuantity;
        // Check both 'unit' (from invoice) and 'uom' (from PO) field names
        if (!quantityUnit) {
          const unitValue = item.unit || item.uom || item.unit_of_measurement;
          if (unitValue) {
            quantityUnit = getUnitLabel(unitValue);
          }
        }
      });
    }
    
    const quantityDisplay = totalQuantity > 0 
      ? `${totalQuantity} ${quantityUnit || ''}`.trim()
      : 'N/A';
    
    const shippingManifest = ewayBillData.shippingManifest || null;
    let manifestStatus = 'Pending';
    let lrNumber = null;
    
    if (shippingManifest) {
      const status = (shippingManifest.status || '').trim();
      if (status.toLowerCase() === 'draft') {
        manifestStatus = 'Draft';
      } else if (status.toLowerCase() === 'submitted') {
        manifestStatus = 'Shipping Manifest Created';
      } else if (status.toLowerCase() === 'accepted') {
        manifestStatus = 'Accepted';
      }
      lrNumber = shippingManifest.lr_number || null;
    }
    
    return {
      id: ewayBill.id,
      ewayNumber: ewayBill.ewb_number || 'N/A',
      ewayBillDate: ewayBill.ewb_date ? formatDate(ewayBill.ewb_date) : 'N/A',
      poNumber: order.po_number || `PO-${order.id}`,
      invoiceNumber: invoice.invoice_number || 'N/A',
      invoiceDate: invoice.invoice_date ? formatDate(invoice.invoice_date) : 'N/A',
      logisticsGenericRfqId: ewayBill.logistics_generic_rfq_id ?? null,
      logisticsQuoteId: ewayBill.logistics_quote_id ?? null,
      productName: productNamesDisplay,
      category: productCategoriesDisplay,
      quantity: quantityDisplay,
      totalAmount: formatCurrency(displayTotalAmount, currencyCode),
      expectedDelivery: poData.expectedDeliveryDate ? formatDate(poData.expectedDeliveryDate) : (order.expected_delivery_date ? formatDate(order.expected_delivery_date) : 'N/A'),
      lineItems: lineItems,
      originalData: order,
      invoiceData: invoice,
      ewayBillData: ewayBill,
      shippingManifest: shippingManifest,
      manifestStatus: manifestStatus,
      lrNumber: lrNumber,
      manifestCreatedDate: shippingManifest?.created_datetime ? formatDate(shippingManifest.created_datetime) : (shippingManifest?.manifest_date ? formatDate(shippingManifest.manifest_date) : 'N/A')
    };
  };

  const getCurrencyLabel = (currencyValue) => {
    if (!currencyValue) return 'INR';
    if (!currencyOptions || currencyOptions.length === 0) return currencyValue;
    
    const foundByValue = currencyOptions.find(opt => 
      String(opt.value) === String(currencyValue) ||
      opt.currencyCode === String(currencyValue)
    );
    if (foundByValue) {
      return foundByValue.currencyCode || foundByValue.label || currencyValue;
    }
    
    const foundByCode = currencyOptions.find(opt => 
      opt.currencyCode === String(currencyValue).toUpperCase()
    );
    if (foundByCode) {
      return foundByCode.currencyCode || foundByCode.label || currencyValue;
    }
    
    return currencyValue;
  };

  const formatCurrency = (amount, currencyCode) => {
    if (!amount && amount !== 0) return 'N/A';
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return 'N/A';
    
    const formatted = numAmount.toLocaleString('en-IN', {
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
    
    const symbol = getCurrencySymbol(currencyCode);
    
    return `${symbol}${formatted}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Filter eway bills by search query
  const filteredEWayBills = acceptedEWayBills.filter(ewayBillData => {
    if (!searchQuery) return true;
    const transformed = transformEWayBillData(ewayBillData);
    const query = searchQuery.toLowerCase();
    return (
      (transformed.ewayNumber && transformed.ewayNumber.toLowerCase().includes(query)) ||
      (transformed.poNumber && transformed.poNumber.toLowerCase().includes(query)) ||
      (transformed.invoiceNumber && transformed.invoiceNumber.toLowerCase().includes(query)) ||
      (transformed.productName && transformed.productName.toLowerCase().includes(query)) ||
      (transformed.category && transformed.category.toLowerCase().includes(query))
    );
  });

  return (
    <div className="invoice-list">
      <div className="il-main-content">
        {/* Header Section */}
        <div className="il-header">
          <div className="il-header-left">
            <nav aria-label="breadcrumb" className="il-breadcrumb">
              <ol className="il-breadcrumb-list">
                <li className="il-breadcrumb-item">
                  <Link to="/dashboard/seller" className="il-breadcrumb-link">Home</Link>
                </li>
                <li className="il-breadcrumb-item il-breadcrumb-active" aria-current="page">
                  Generate Shipping Manifest
                </li>
              </ol>
            </nav>
            <h1 className="il-main-title">Generate Shipping Manifest</h1>
            <p className="il-subtitle">Generate shipping manifest for generated E-Way Bills</p>
          </div>
          <div className="il-header-right">
            <button className="il-back-btn" onClick={() => navigate('/dashboard/seller')}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
              Back
            </button>
          </div>
        </div>

        {/* Search Section */}
        <div className="il-search-filter-section">
          <div className="il-search-header">
            <h2 className="il-search-title">Search Generated E-Way Bills</h2>
          </div>
          <div className="il-search-filter-row">
            <div className="il-search-wrapper">
              <svg 
                className="il-search-icon" 
                xmlns="http://www.w3.org/2000/svg" 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="il-search-input"
                placeholder="Search by E-Way Bill number, invoice number, PO number, product name, or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* E-Way Bill Cards */}
        <div className="il-invoices-container">
          {loading ? (
            <div className="il-empty-state">
              <p>Loading generated E-Way Bills...</p>
            </div>
          ) : filteredEWayBills.length === 0 ? (
            <div className="il-empty-state">
              <p>No generated E-Way Bills found.</p>
            </div>
          ) : (
            filteredEWayBills.map(ewayBillData => {
              const transformed = transformEWayBillData(ewayBillData);
              return (
                <div key={transformed.id} className="il-invoice-card">
                  <div className="il-card-content-wrapper">
                    <div className="il-card-main-row">
                      <div className="il-card-left-section">
                        <div className="il-invoice-header-row">
                          <div className="il-invoice-header-left">
                            <svg 
                              className="il-document-icon"
                              xmlns="http://www.w3.org/2000/svg" 
                              width="24" 
                              height="24" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            >
                              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                              <path d="M10 9H8"></path>
                              <path d="M16 13H8"></path>
                              <path d="M16 17H8"></path>
                            </svg>
                            <div className="il-invoice-title-group">
                              <div className="il-title-row">
                                <h3 className="il-invoice-number">
                                  {transformed.lrNumber || transformed.ewayNumber}
                                </h3>
                                <span className={`il-status-badge ${
                                  transformed.manifestStatus === 'Pending' ? 'il-status-pending' :
                                  transformed.manifestStatus === 'Draft' ? 'il-status-draft' :
                                  transformed.manifestStatus === 'Shipping Manifest Created' ? 'il-status-created' :
                                  transformed.manifestStatus === 'Accepted' ? 'il-status-accepted' : 'il-status-pending'
                                }`}>
                                  {transformed.manifestStatus}
                                </span>
                              </div>
                              <p className="il-invoice-number-subtitle">
                                {transformed.lrNumber ? (
                                  <>
                                    E-Way Bill: <span className="il-invoice-number-value">{transformed.ewayNumber}</span>
                                    {' | '}
                                    Invoice: <span className="il-invoice-number-value">{transformed.invoiceNumber}</span>
                                  </>
                                ) : (
                                  <>
                                    E-Way Bill: <span className="il-invoice-number-value">{transformed.ewayNumber}</span>
                                    {' | '}
                                    Invoice: <span className="il-invoice-number-value">{transformed.invoiceNumber}</span>
                                  </>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="il-card-actions">
                            {/* Show Generate Shipping Manifest button only for Pending status */}
                            {transformed.manifestStatus === 'Pending' && (
                              <button 
                                className="il-action-btn il-btn-eway"
                                onClick={() => navigate('/shipping-manifest-create', { 
                                  state: { 
                                    ewayBillId: transformed.id,
                                    ewayNumber: transformed.ewayNumber,
                                    invoiceId: transformed.invoiceData?.id,
                                    invoiceNumber: transformed.invoiceNumber,
                                    logisticsGenericRfqId: transformed.logisticsGenericRfqId,
                                    logisticsQuoteId: transformed.logisticsQuoteId,
                                  } 
                                })}
                              >
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                                  <line x1="12" y1="22.08" x2="12" y2="12"></line>
                                </svg>
                                Generate Shipping Manifest
                              </button>
                            )}
                            
                            {/* Show Edit Manifest button for Draft status */}
                            {transformed.manifestStatus === 'Draft' && transformed.shippingManifest && (
                              <button 
                                className="il-action-btn il-btn-edit"
                                onClick={() => navigate('/shipping-manifest-create', { 
                                  state: { 
                                    ewayBillId: transformed.id,
                                    ewayNumber: transformed.ewayNumber,
                                    invoiceId: transformed.invoiceData?.id,
                                    invoiceNumber: transformed.invoiceNumber,
                                    logisticsGenericRfqId: transformed.logisticsGenericRfqId,
                                    logisticsQuoteId: transformed.logisticsQuoteId,
                                    manifestId: transformed.shippingManifest.id
                                  } 
                                })}
                              >
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                                Edit Manifest
                              </button>
                            )}
                            
                            {/* Show View button for Shipping Manifest Created and Accepted statuses */}
                            {(transformed.manifestStatus === 'Shipping Manifest Created' || transformed.manifestStatus === 'Accepted') && transformed.shippingManifest && (
                              <button 
                                className="il-action-btn il-btn-view"
                                onClick={async () => {
                                  // Fetch full manifest details with items
                                  try {
                                    const response = await CommonService.get(`/shipping-manifest/${transformed.shippingManifest.id}`);
                                    if (response && response.success && response.data) {
                                      setSelectedManifest(response.data);
                                      setSelectedManifestEWayBillNumber(transformed.ewayNumber);
                                      setShowManifestModal(true);
                                    } else {
                                      // Fallback to existing manifest data
                                      setSelectedManifest(transformed.shippingManifest);
                                      setSelectedManifestEWayBillNumber(transformed.ewayNumber);
                                      setShowManifestModal(true);
                                    }
                                  } catch (error) {
                                    console.error('Error fetching manifest details:', error);
                                    // Fallback to existing manifest data
                                    setSelectedManifest(transformed.shippingManifest);
                                    setSelectedManifestEWayBillNumber(transformed.ewayNumber);
                                    setShowManifestModal(true);
                                  }
                                }}
                              >
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                  <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                                View
                              </button>
                            )}
                            
                            {/* Show View Acceptance Details button only for Accepted status */}
                            {transformed.manifestStatus === 'Accepted' && transformed.shippingManifest && (
                              <button 
                                className="il-action-btn il-btn-acceptance"
                                onClick={async () => {
                                  // Fetch full manifest details with acceptance information
                                  try {
                                    const response = await CommonService.get(`/shipping-manifest/${transformed.shippingManifest.id}`);
                                    if (response && response.success && response.data) {
                                      setSelectedManifest(response.data);
                                      setShowAcceptanceModal(true);
                                    } else {
                                      // Fallback to existing manifest data
                                      setSelectedManifest(transformed.shippingManifest);
                                      setShowAcceptanceModal(true);
                                    }
                                  } catch (error) {
                                    console.error('Error fetching manifest acceptance details:', error);
                                    // Fallback to existing manifest data
                                    setSelectedManifest(transformed.shippingManifest);
                                    setShowAcceptanceModal(true);
                                  }
                                }}
                              >
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                </svg>
                                View Acceptance Details
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="il-details-grid">
                          <div className="il-grid-item">
                            <p className="il-detail-label">Product Name</p>
                            <p className="il-detail-value" title={transformed.productName}>{transformed.productName}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">Category</p>
                            <p className="il-detail-value">{transformed.category}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">Quantity</p>
                            <p className="il-detail-value">{transformed.quantity}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">Total Amount</p>
                            <p className="il-detail-value">{transformed.totalAmount}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">Expected Delivery Date</p>
                            <p className="il-detail-value">{transformed.expectedDelivery}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">
                              {transformed.manifestStatus === 'Draft' || transformed.manifestStatus === 'Shipping Manifest Created' 
                                ? 'Shipping Manifest Created Date' 
                                : 'E-Way Bill Date'}
                            </p>
                            <p className="il-detail-value">
                              {transformed.manifestStatus === 'Draft' || transformed.manifestStatus === 'Shipping Manifest Created' 
                                ? transformed.manifestCreatedDate 
                                : transformed.ewayBillDate}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Manifest View Modal */}
      {showManifestModal && selectedManifest && (
        <div className="sm-preview-modal-overlay" onClick={() => setShowManifestModal(false)}>
          <div className="sm-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sm-preview-modal-header">
              <div className="sm-preview-modal-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm-preview-icon">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <div>
                  <h2 className="sm-preview-modal-title">Shipping Manifest Details</h2>
                  <p className="sm-preview-modal-subtitle">View complete shipping manifest information</p>
                </div>
              </div>
              <button className="sm-preview-modal-close" onClick={() => setShowManifestModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="sm-preview-modal-body">
              {/* Manifest Information */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title">Manifest Information</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>Manifest Number</label>
                    <p>{selectedManifest.manifest_number || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>LR Number</label>
                    <p>{selectedManifest.lr_number || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Manifest Date</label>
                    <p>{selectedManifest.manifest_date ? formatDate(selectedManifest.manifest_date) : 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Status</label>
                    <p>{selectedManifest.status || 'N/A'}</p>
                  </div>
                </div>
              </div>
              
              {/* From (Exporter/Shipper) */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-orange">From (Exporter/Shipper)</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>Company / Shipper Name</label>
                    <p>{selectedManifest.from_company_name || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Contact Person</label>
                    <p>{selectedManifest.from_contact_person || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>GSTIN</label>
                    <p>{selectedManifest.from_gstin || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>IEC / Export Code</label>
                    <p>{selectedManifest.from_iec || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Address</label>
                    <p>{selectedManifest.from_address_line1 || 'N/A'} {selectedManifest.from_address_line2 && selectedManifest.from_address_line2}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Location</label>
                    <p>{selectedManifest.from_city || 'N/A'}, {selectedManifest.from_state || 'N/A'}, {selectedManifest.from_country || 'N/A'} - {selectedManifest.from_postal_code || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Email</label>
                    <p>{selectedManifest.from_email || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Phone</label>
                    <p>{selectedManifest.from_phone || 'N/A'}</p>
                  </div>
                </div>
              </div>
              
              {/* To (Consignee/Importer) */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-green">To (Consignee/Importer)</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>Company / Consignee Name</label>
                    <p>{selectedManifest.to_company_name || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Contact Person</label>
                    <p>{selectedManifest.to_contact_person || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Tax ID / Import Code</label>
                    <p>{selectedManifest.to_tax_id || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Email</label>
                    <p>{selectedManifest.to_email || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Address</label>
                    <p>{selectedManifest.to_address_line1 || 'N/A'} {selectedManifest.to_address_line2 && selectedManifest.to_address_line2}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Location</label>
                    <p>{selectedManifest.to_city || 'N/A'}, {selectedManifest.to_state || 'N/A'}, {selectedManifest.to_country || 'N/A'} - {selectedManifest.to_postal_code || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Phone</label>
                    <p>{selectedManifest.to_phone || 'N/A'}</p>
                  </div>
                </div>
              </div>
              
              {/* Order Reference Details */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-blue">Order Reference Details</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>PO Number</label>
                    <p>{selectedManifest.po_number || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Order Date</label>
                    <p>{selectedManifest.order_date ? formatDate(selectedManifest.order_date) : 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Currency</label>
                    <p>{selectedManifest.currency || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Subtotal Amount</label>
                    <p>{selectedManifest.subtotal_amount ? formatCurrency(selectedManifest.subtotal_amount, selectedManifest.currency || 'INR') : 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Buyer Name</label>
                    <p>{selectedManifest.buyer_name || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Supplier Name</label>
                    <p>{selectedManifest.supplier_name || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Commercial Invoice Number</label>
                    <p>{selectedManifest.commercial_invoice_number || 'N/A'}</p>
                  </div>
                </div>
              </div>
              
              {/* Shipment Details */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-purple">Shipment Details</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>Preferred Delivery Port</label>
                    <p>{selectedManifest.preferred_delivery_port || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Delivery Port Reference</label>
                    <p>{selectedManifest.delivery_port_reference || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Invoice/Bill Number</label>
                    <p>{selectedManifest.invoice_bill_number || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>E-way Bill Number</label>
                    <p>{selectedManifestEWayBillNumber || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Mode of Transport</label>
                    <p>{(() => {
                      if (!selectedManifest.mode_of_transport) return 'N/A';
                      if (modeOfTransportOptions && modeOfTransportOptions.length > 0) {
                        const found = modeOfTransportOptions.find(opt => 
                          String(opt.value) === String(selectedManifest.mode_of_transport) ||
                          opt.label === selectedManifest.mode_of_transport
                        );
                        return found ? found.label : selectedManifest.mode_of_transport;
                      }
                      return selectedManifest.mode_of_transport;
                    })()}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Incoterms</label>
                    <p>{(() => {
                      if (!selectedManifest.incoterms) return 'N/A';
                      if (shippingTermsOptions && shippingTermsOptions.length > 0) {
                        const found = shippingTermsOptions.find(opt => 
                          String(opt.value) === String(selectedManifest.incoterms) ||
                          opt.label === selectedManifest.incoterms
                        );
                        return found ? found.label : selectedManifest.incoterms;
                      }
                      return selectedManifest.incoterms;
                    })()}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Estimated Delivery Date</label>
                    <p>{selectedManifest.estimated_delivery_date ? formatDate(selectedManifest.estimated_delivery_date) : 'N/A'}</p>
                  </div>
                </div>
              </div>
              
              {/* Vehicle & Driver Details */}
              <div className="sm-preview-section">
                <h3 className="sm-preview-section-title sm-preview-section-title-indigo">Vehicle & Driver Details</h3>
                <div className="sm-preview-grid">
                  <div className="sm-preview-item">
                    <label>Vehicle Type</label>
                    <p>{selectedManifest.vehicle_type || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Vehicle Number</label>
                    <p>{selectedManifest.vehicle_number || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Vehicle Capacity</label>
                    <p>{selectedManifest.vehicle_capacity || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>GPS Tracking ID</label>
                    <p>{selectedManifest.gps_tracking_id || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>GPS Provider</label>
                    <p>{selectedManifest.gps_provider || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Insurance Policy No.</label>
                    <p>{selectedManifest.insurance_policy_no || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Insurance Provider</label>
                    <p>{selectedManifest.insurance_provider || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Driver Name</label>
                    <p>{selectedManifest.driver_name || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Driver License No.</label>
                    <p>{selectedManifest.driver_license_no || 'N/A'}</p>
                  </div>
                  <div className="sm-preview-item">
                    <label>Driver Contact Number</label>
                    <p>{selectedManifest.driver_contact_number || 'N/A'}</p>
                  </div>
                </div>
              </div>
              
              {/* Items & Packages Details */}
              {selectedManifest.items && selectedManifest.items.length > 0 && (
                <div className="sm-preview-section">
                  <h3 className="sm-preview-section-title sm-preview-section-title-orange">Items & Packages Details</h3>
                  <div className="sm-preview-table-wrapper">
                    <table className="sm-preview-table">
                      <thead>
                        <tr>
                          <th>HSN/Product Code</th>
                          <th>Product Description</th>
                          <th>Qty Shipped</th>
                          <th>Qty Total</th>
                          <th>Shipping Qty</th>
                          <th>UOM</th>
                          <th>Package Type</th>
                          <th>No. Packages</th>
                          <th>Weight/Pkg (kg)</th>
                          <th>Total Weight</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedManifest.items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.hsn_product_code || 'N/A'}</td>
                            <td>{item.product_description || 'N/A'}</td>
                            <td>{item.qty_shipped || 'N/A'}</td>
                            <td>{item.qty_total || 'N/A'}</td>
                            <td>{item.shipping_qty || 'N/A'}</td>
                            <td>{getUnitLabel(item.uom) || '-'}</td>
                            <td>{item.package_type || 'N/A'}</td>
                            <td>{item.no_packages || 'N/A'}</td>
                            <td>{item.weight_per_pkg_kg || 'N/A'}</td>
                            <td>{item.total_weight_kg || 'N/A'} kg</td>
                          </tr>
                        ))}
                        <tr className="sm-preview-total-row">
                          <td colSpan="1"></td>
                          <td className="sm-totals-label">Totals:</td>
                          <td colSpan="5"></td>
                          <td className="sm-total-value">{selectedManifest.total_packages || 0}</td>
                          <td colSpan="1"></td>
                          <td className="sm-total-value">{selectedManifest.total_weight_kg || 0} kg</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Goods Handling Requirements */}
              {selectedManifest.goods_handling_requirements && (
                <div className="sm-preview-section">
                  <h3 className="sm-preview-section-title sm-preview-section-title-red">Goods Handling Requirements</h3>
                  <div className="sm-preview-grid">
                    {(() => {
                      const requirements = typeof selectedManifest.goods_handling_requirements === 'string' 
                        ? JSON.parse(selectedManifest.goods_handling_requirements) 
                        : selectedManifest.goods_handling_requirements;
                      return (
                        <>
                          <div className="sm-preview-item">
                            <label>Hazardous Material</label>
                            <p>{requirements.hazardousMaterial ? 'Yes' : 'No'}</p>
                          </div>
                          {requirements.hazardousMaterial && (
                            <>
                              <div className="sm-preview-item">
                                <label>Hazard Class</label>
                                <p>{requirements.hazardClass || 'N/A'}</p>
                              </div>
                              <div className="sm-preview-item">
                                <label>UN Number</label>
                                <p>{requirements.unNumber || 'N/A'}</p>
                              </div>
                            </>
                          )}
                          <div className="sm-preview-item">
                            <label>Temperature Controlled</label>
                            <p>{requirements.temperatureControlled ? 'Yes' : 'No'}</p>
                          </div>
                          {requirements.temperatureControlled && (
                            <>
                              <div className="sm-preview-item">
                                <label>Min Temperature</label>
                                <p>{requirements.minTemperature || 'N/A'}</p>
                              </div>
                              <div className="sm-preview-item">
                                <label>Max Temperature</label>
                                <p>{requirements.maxTemperature || 'N/A'}</p>
                              </div>
                            </>
                          )}
                          <div className="sm-preview-item">
                            <label>Fragile</label>
                            <p>{requirements.fragile ? 'Yes' : 'No'}</p>
                          </div>
                          {requirements.specialHandling && (
                            <div className="sm-preview-item sm-preview-item-full">
                              <label>Special Handling</label>
                              <p>{requirements.specialHandling}</p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
              
              {/* Additional Notes */}
              {selectedManifest.additional_notes && (
                <div className="sm-preview-section">
                  <h3 className="sm-preview-section-title sm-preview-section-title-gray">Additional Notes / Remarks</h3>
                  <p className="sm-preview-text">{selectedManifest.additional_notes}</p>
                </div>
              )}
            </div>
            <div className="sm-preview-modal-footer">
              <button className="sm-btn sm-btn-secondary" onClick={() => setShowManifestModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Acceptance Details Modal */}
      {showAcceptanceModal && selectedManifest && (
        <div className="sm-preview-modal-overlay" onClick={() => setShowAcceptanceModal(false)}>
          <div className="sm-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sm-preview-modal-header">
              <div className="sm-preview-modal-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm-preview-icon">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <div>
                  <h2 className="sm-preview-modal-title">Acceptance Details</h2>
                  <p className="sm-preview-modal-subtitle">View buyer acceptance information</p>
                </div>
              </div>
              <button className="sm-preview-modal-close" onClick={() => setShowAcceptanceModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="sm-preview-modal-body">
              {(() => {
                // Get inspection checklist from separate column
                let inspectionChecklist = {};
                if (selectedManifest.inspection_checklist) {
                  if (typeof selectedManifest.inspection_checklist === 'string') {
                    try {
                      inspectionChecklist = JSON.parse(selectedManifest.inspection_checklist);
                    } catch (e) {
                      console.warn('Failed to parse inspection_checklist:', e);
                    }
                  } else if (typeof selectedManifest.inspection_checklist === 'object') {
                    inspectionChecklist = selectedManifest.inspection_checklist;
                  }
                }
                
                // Fallback: Try to parse from acceptance_notes for old records (backward compatibility)
                let additionalNotes = selectedManifest.acceptance_notes || null;
                if (!selectedManifest.inspection_checklist && selectedManifest.acceptance_notes) {
                  try {
                    const parsed = JSON.parse(selectedManifest.acceptance_notes);
                    if (parsed && typeof parsed === 'object') {
                      if (parsed.inspectionChecklist && typeof parsed.inspectionChecklist === 'object') {
                        inspectionChecklist = parsed.inspectionChecklist;
                      }
                      if (parsed.notes !== undefined) {
                        additionalNotes = parsed.notes || null;
                      }
                    }
                  } catch (e) {
                    // If not JSON, treat as plain text notes
                    additionalNotes = selectedManifest.acceptance_notes;
                  }
                }

                return (
                  <>
                    {/* Receiving Details */}
                    <div className="sm-preview-section">
                      <h3 className="sm-preview-section-title sm-preview-section-title-green">Receiving Details</h3>
                      <div className="sm-preview-grid">
                        <div className="sm-preview-item">
                          <label>Date Received</label>
                          <p>{selectedManifest.actual_arrival_date ? formatDate(selectedManifest.actual_arrival_date) : 'N/A'}</p>
                        </div>
                        <div className="sm-preview-item">
                          <label>Accepted Date</label>
                          <p>{selectedManifest.accepted_date ? formatDate(selectedManifest.accepted_date) : 'N/A'}</p>
                        </div>
                        <div className="sm-preview-item">
                          <label>Received By</label>
                          <p>{selectedManifest.accepted_by || 'N/A'}</p>
                        </div>
                        <div className="sm-preview-item">
                          <label>Packages Received</label>
                          <p>{selectedManifest.total_packages || 'N/A'}</p>
                        </div>
                        <div className="sm-preview-item">
                          <label>Condition</label>
                          <p>Good Condition</p>
                        </div>
                      </div>
                    </div>

                    {/* Inspection Checklist */}
                    <div className="sm-preview-section">
                      <h3 className="sm-preview-section-title sm-preview-section-title-orange">Inspection Checklist</h3>
                      {(() => {
                        const checklistItems = [
                          { key: 'packagingIntact', label: 'Packaging is intact and undamaged' },
                          { key: 'correctQuantity', label: 'Correct quantity received as per manifest' },
                          { key: 'noVisibleDamage', label: 'No visible damage to goods' },
                          { key: 'sealsIntact', label: 'Container seals are intact and match manifest' },
                          { key: 'documentsComplete', label: 'All shipping documents are complete and correct' }
                        ];
                        
                        // Filter to show only checked items
                        const checkedItems = checklistItems.filter(item => 
                          inspectionChecklist[item.key]
                        );

                        if (checkedItems.length === 0) {
                          return (
                            <p style={{ fontStyle: 'italic', color: '#666', padding: '1rem' }}>
                              No inspection items were checked.
                            </p>
                          );
                        }

                        return (
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {checkedItems.map(item => (
                              <li
                                key={item.key}
                                style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  padding: '0.5rem 0',
                                  gap: '0.75rem'
                                }}
                              >
                                <span style={{ color: '#10b981', flexShrink: 0 }}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 6 9 17l-5-5" />
                                  </svg>
                                </span>
                                <span>{item.label}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      })()}
                    </div>

                    {/* Additional Notes */}
                    {additionalNotes && (
                      <div className="sm-preview-section">
                        <h3 className="sm-preview-section-title sm-preview-section-title-gray">Additional Notes</h3>
                        <p className="sm-preview-text">{additionalNotes}</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="sm-preview-modal-footer">
              <button className="sm-btn sm-btn-secondary" onClick={() => setShowAcceptanceModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
